/**
 * Per-run board visit snapshots in localStorage. Used to show a "updates"
 * notification on Kanban cards when TEXEC / video / docs progress advanced
 * since the user last left that run.
 */

const STORAGE_KEY = 'sneaky:board:run-snapshots';

export interface BoardRunSnapshot {
  texecDone: number;
  texecTotal: number;
  videoAnalyzed: number;
  videoTotal: number;
  docsReady: boolean;
  bugsInReview: number;
  savedAt: string;
}

function readAll(): Record<string, BoardRunSnapshot> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, BoardRunSnapshot>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, BoardRunSnapshot>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // quota / private mode — ignore
  }
}

export function saveBoardRunSnapshot(runId: string, snap: Omit<BoardRunSnapshot, 'savedAt'>) {
  if (!runId) return;
  const all = readAll();
  all[runId] = { ...snap, savedAt: new Date().toISOString() };
  writeAll(all);
}

export function getBoardRunSnapshot(runId: string): BoardRunSnapshot | null {
  if (!runId) return null;
  return readAll()[runId] ?? null;
}

export function boardHasUpdatesSinceVisit(
  runId: string | null,
  current: {
    texecDone: number;
    videoAnalyzed: number;
    docsReady: boolean;
    bugsInReview: number;
  },
): boolean {
  if (!runId) return false;
  const prev = getBoardRunSnapshot(runId);
  if (!prev) return false;
  return (
    current.texecDone > prev.texecDone ||
    current.videoAnalyzed > prev.videoAnalyzed ||
    (current.docsReady && !prev.docsReady) ||
    current.bugsInReview > prev.bugsInReview
  );
}
